import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { FilterCompanyQuarterlyAnalyticsWebPage } from 'commons/service/bank/companyDetails'
import { CompanyDetailsWebPage } from 'commons/types/DTO/bank'
import {
  CompanyQuarterlyAnalyticsWebPage,
  QuarterlyAnalytics
} from 'commons/types/DTO/bank/companyDetails/quarterlyAnalytics'
import { TableDataQa } from 'commons/types/enums'
import { Request } from 'commons/utils/request/request'
import { Layer, Table } from 'ui/components'
import { PageSectionPrimary } from 'ui/components/Page'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'
import { useDeleteQR } from '~bank-quarterly-report/hooks/qr'

import { handlerServerError } from '@orbit/errors'

import { columns } from './columns'

type Props = {
  data: CompanyDetailsWebPage
}

export const CompanyQRsTable: FC<Props> = ({ data }) => {
  const { showQuarterlyAnalytics: initialQuarterlyAnalytics, showQuarterlyAnalyticsTotalCount, id } = data
  const [quarterlyAnalytics, setQuarterlyAnalytics] = useState(initialQuarterlyAnalytics)
  const [totalCount, setTotalCount] = useState(showQuarterlyAnalyticsTotalCount)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: id,
      page: 1,
      size: 10
    }),
    [id]
  )

  const [tableFilter, setTableFilter] = useState<FilterCompanyQuarterlyAnalyticsWebPage>(defaultFilter)
  const tableState = useTableState({ filter: {} })

  const { mutate: filterCompanyQuarterlyAnalyticsWebPage, isPending } = useMutation<
    CompanyQuarterlyAnalyticsWebPage,
    Error,
    FilterCompanyQuarterlyAnalyticsWebPage
  >({
    mutationFn: (opts) => Request.post(Request.urls.bank.companyQuarterlyAnalyticsWebpage, opts),
    onSuccess: (result) => {
      setQuarterlyAnalytics(result.quarterlyAnalytics)
      setTotalCount(result.quarterlyAnalyticsTotalCount)
    },
    onError: (error) => handlerServerError(error)
  })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      tableState.onPageChange(page, size)

      const newFilter = {
        ...tableFilter,
        page,
        size
      }

      setTableFilter(newFilter)
      filterCompanyQuarterlyAnalyticsWebPage(newFilter)
    },
    [filterCompanyQuarterlyAnalyticsWebPage, tableFilter, tableState]
  )

  useEffect(() => {
    setQuarterlyAnalytics(initialQuarterlyAnalytics)
  }, [initialQuarterlyAnalytics])

  const [tbd, setTbd] = useState<any>(null)

  const { deleteQR } = useDeleteQR({
    onSuccess: () => filterCompanyQuarterlyAnalyticsWebPage(tableFilter),
    onError: (e) => handlerServerError(e)
  })

  const { open, Confirmation } = useConfirmation({
    message: `Are you sure you want to remove this report?`,
    onConfirm: () => deleteQR({ id: tbd.id }),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const startDeleting = useCallback(
    (item: any) => {
      setTbd(item)
      open()
    },
    [open, setTbd]
  )

  return (
    <PageSectionPrimary title="Quarterly Analytics" anchor="qr" face="outlined">
      <Confirmation />
      <Layer rounded shadowed stretch>
        <Table<QuarterlyAnalytics>
          dataQa={TableDataQa.BP_COMPANY_QUARTERLY_ANALYTICS}
          columns={columns(startDeleting)}
          dataSource={quarterlyAnalytics.map((el) => ({
            item: el
          }))}
          pagination={{
            page: tableState.page,
            size: tableState.size,
            onChange: changePagination,
            total: totalCount
          }}
          loading={isPending}
        />
      </Layer>
    </PageSectionPrimary>
  )
}
